import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { useTranslation } from "react-i18next";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { Alert } from "~/common/mui/Alert/Alert";
import { Box, Stack, styled } from "~/common/mui";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import Spinner from "~/common/components/Spinner/Spinner";
import { TopFilterBar } from "./components/TopFilterBar";
import { SkeletonResultList } from "./views/ListView/components/SkeletonResultList";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useSearchPagination } from "./useSearchPagination";
import { L1CategoryNavigation } from "../L1CategoryNavigation";
import { NewDesignReduxProductTabularView } from "./views/NewTabularView/NewTabularView";
import { NewTabularViewSkeleton } from "./views/NewTabularView/NewTabularViewSkeleton";
import { memo, type ReactNode, useCallback } from "react";
import { SearchDisplayMode } from "~/features/search/models/models";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { clearAll, clearAllTechFilters, clearAllFacetFilters } from "~/features/search/redux/actions";
import { Pagination } from "~/features/search/components/common/Pagination";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { useDispatch, useSelector } from "react-redux";
import { SeriesTreeView } from "~/features/search/components/ProductResults/views/SeriesTreeView/SeriesTreeView";
import { selectIsInFlight, selectIsInit, selectProductResults } from "~/features/search/redux/searchSelectors";
import NotFoundContactUsBar from "~/features/search/components/common/NotFoundContactUsBar/NotFoundContactUsBar";
import SearchResultList from "~/features/search/components/ProductResults/views/ListView/SearchResultList";
import { useResetQualtrics } from "~/features/Analytics/hooks/useResetQualtrics";
import { useReduxUnitMode } from "~/domains/userPreferences/hooks/useReduxUnitMode";
import { NoSearchResults } from "~/features/search/components/common/NoSearchResults";
import type { RootState } from "~/app/store";

import { useSRPResultsViewMode } from "./useSRPResultsViewMode";
import { useInitializeSRPResultsViewMode } from "./useInitializeSRPResultsViewMode";
import { getTableFullbleedStyle } from "./fullbleedUtils";
import { usePriceReferenceSelection } from "~/app/config/useSession";

interface Props {
  recommendationPosition?: number;
  recommendationComponent?: ReactNode;
}

const ProductResults = ({ recommendationPosition, recommendationComponent }: Props) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();
  const screenSize = useNumericScreenSize();
  const dispatch = useDispatch();
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const results = useSelector(selectProductResults);
  const appliedFilters = useSelectAppliedFilters();

  const pageType = useSearchPageType();
  // TODO - other cases! Campaign page?
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";

  const inFlight = useSelector(selectIsInFlight);
  const isInit = useSelector(selectIsInit);

  const [displayMode, setDisplayMode] = useSRPResultsViewMode();

  const productCodes = useSelector((state: RootState) => state.srp.results.results.map((row) => row.productCode));

  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(selection),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const hasSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
  );

  const tariffSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
  );

  useInitializeSRPResultsViewMode(isInit, isProductFamilySelected, setDisplayMode);

  const { pageAmount, currentPage, onPageChanged } = useSearchPagination();

  useResetQualtrics([inFlight]);

  const { value: unitMode, onChange: setUnitMode } = useReduxUnitMode(gaInteractionType, () => {
    dispatch(clearAllTechFilters());
    dispatch(clearAllFacetFilters(unitMode));
  });

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  const tabularViewConfigured = isProductFamilySelected || pageType === SearchPageType.PLP;
  const showChildCategoryNavigation = !isProductFamilySelected && pageType == SearchPageType.SEARCH;

  const showSkeletons = displayMode === SearchDisplayMode.LIST || displayMode === SearchDisplayMode.TABULAR;

  let paginationComponent = null;
  if (pageAmount > 1) {
    paginationComponent = (
      <ProductResultsPagination>
        <Pagination
          classes={{ root: "scha__srp__pagination" }}
          pageAmount={pageAmount}
          currentPage={currentPage}
          onPageChange={onPageChanged}
          renderHrefs={true}
          goToIdAfterChange={"#resultsHeader"}
        />
      </ProductResultsPagination>
    );
  }
  if (displayMode === SearchDisplayMode.TABULAR) {
    // Pagination handled by the new tabular view
    paginationComponent = null;
  }

  if (!isInit || results.length) {
    return (
      <ProductResultsContainer id={"productResults"}>
        {isInit && (
          <TopFilterBar
            unitMode={unitMode}
            displayMode={displayMode}
            setUnitMode={setUnitMode}
            setDisplayMode={setDisplayMode}
          />
        )}

        {showChildCategoryNavigation && <L1CategoryNavigation />}

        {inFlight && !showSkeletons && <ResultListSpinner className="spinner--result-list" />}

        {(!inFlight || showSkeletons) && (
          <>
            {displayMode === SearchDisplayMode.TABULAR && (
              <>
                {inFlight && (
                  <Box sx={getTableFullbleedStyle(screenSize)}>
                    <NewTabularViewSkeleton />
                  </Box>
                )}
                {!inFlight && (
                  <Stack gap={4} sx={getTableFullbleedStyle(screenSize)}>
                    {tabularViewConfigured && <NewDesignReduxProductTabularView />}
                    {!tabularViewConfigured && (
                      <SelectCategoryAlert severity={"info"} className={"scha__srp__select-category-msg"}>
                        {t("page.srp.selectCategory")}
                      </SelectCategoryAlert>
                    )}
                  </Stack>
                )}
              </>
            )}
            {displayMode === SearchDisplayMode.LIST && (
              <>
                {inFlight ? (
                  <SkeletonResultList
                    amount={20}
                    recommendationPosition={recommendationPosition}
                    recommendationComponent={recommendationComponent}
                  />
                ) : (
                  <>
                    <SearchResultList
                      results={results}
                      recommendationPosition={recommendationPosition}
                      recommendationComponent={recommendationComponent}
                    />
                    {paginationComponent}
                  </>
                )}
              </>
            )}
            {displayMode === SearchDisplayMode.SERIES_TREE && <SeriesTreeView />}

            {!inFlight && (
              <>
                <NotFoundDisclosure className="scha__srp__not-found-disc mb-2" />
                <ResultsDisclaimers className="scha__srp__disclaimers">
                  <Alert severity={"info"} className={"scha__srp__action-bar__imperial-msg"}>
                    {t("page.srp.unitTypeMessage")}
                  </Alert>
                  <DisclaimerCountryOrigin />
                </ResultsDisclaimers>
                <IncludesSurchargeDisclaimer
                  hasSurcharge={hasSurcharge}
                  tariffSurcharge={tariffSurcharge}
                  className={"mt-30"}
                />
              </>
            )}
          </>
        )}

        <button hidden type="submit" />
      </ProductResultsContainer>
    );
  }

  return (
    <div id={"productResults"}>
      <NoSearchResults variant={appliedFilters.length === 0 ? "search" : "filter"} onClearAll={handleClearAll} />
      <button hidden type="submit" />
    </div>
  );
};

export const selectCurrentPage = (state: RootState) => state.srp.searchParams.currentPage;
const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;

export default memo(ProductResults);

const ResultListSpinner = styled(Spinner)(({ theme }) => ({
  marginTop: theme.spacing(1.25),
}));

const SelectCategoryAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(-1.25),
}));

const NotFoundDisclosure = styled(NotFoundContactUsBar)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const ResultsDisclaimers = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const ProductResultsPagination = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const ProductResultsContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
  "--container": "1208px",
  [theme.breakpoints.up("desktop-s")]: {
    gap: theme.spacing(3),
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--container": "1488px",
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--container": "1792px",
  },
}));
